import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Person,
  Description,
  AttachFile,
  Refresh
} from '@mui/icons-material';
import { apiService } from '../../config/api';

interface Application {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  institution: string;
  description?: string;
  verificationDocuments?: Array<{
    name: string;
    size: number;
    type: string;
    data: string;
  }>;
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string;
  reviewedAt?: string;
  reviewNotes?: string;
}

interface ResearcherApplicationsProps {
  onApplicationProcessed: () => void;
}

const ResearcherApplications: React.FC<ResearcherApplicationsProps> = ({ onApplicationProcessed }) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [resetting, setResetting] = useState<string | null>(null);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      // Load only pending applications
      const response = await apiService.get('/api/admin/researcher-applications?status=pending');
      
      if (response.success) {
        setApplications(response.applications || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleResetApplication = async (applicationId: string) => {
    try {
      setResetting(applicationId);
      const response = await apiService.put(`/api/admin/researcher-applications/${applicationId}/reset`);
      
      if (response.success) {
        // Reload applications to reflect the change
        await loadApplications();
        onApplicationProcessed();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reset application');
    } finally {
      setResetting(null);
    }
  };

  const handleViewApplication = (application: Application) => {
    setSelectedApplication(application);
  };

  const handleReviewApplication = (application: Application, action: 'approve' | 'reject') => {
    setSelectedApplication(application);
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedApplication) return;

    // Validate rejection reason is required
    if (reviewAction === 'reject' && !reviewNotes.trim()) {
      setError('Rejection reason is required when rejecting an application.');
      return;
    }

    try {
      setProcessing(true);
      
      // Use unified /review endpoint for both approve and reject
      const response = await apiService.put(`/api/admin/researcher-applications/${selectedApplication.id}/review`, {
        action: reviewAction, // 'approve' or 'reject'
        reviewNotes
      });

      if (response.success) {
        // Remove the processed application from the list
        setApplications(prev => prev.filter(app => app.id !== selectedApplication.id));
        setReviewDialogOpen(false);
        setSelectedApplication(null);
        onApplicationProcessed();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process application');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (applications.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom>
          No Pending Applications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All researcher applications have been processed.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Applicant</TableCell>
              <TableCell>Institution</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map((application) => (
              <TableRow key={application.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">
                      {application.firstName} {application.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {application.email}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {application.institution}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {application.description || 'No description provided'}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Chip
                    label={application.status.toUpperCase()}
                    color={application.status === 'pending' ? 'warning' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(application.submittedAt)}
                  </Typography>
                </TableCell>
                
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        onClick={() => handleViewApplication(application)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    
                    {application.status === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleReviewApplication(application, 'approve')}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Reject">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleReviewApplication(application, 'reject')}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    
                    {application.status === 'rejected' && (
                      <Tooltip title="Reset for Resubmission">
                        <IconButton 
                          size="small" 
                          color="primary"
                          disabled={resetting === application.id}
                          onClick={() => handleResetApplication(application.id)}
                        >
                          {resetting === application.id ? <CircularProgress size={20} /> : <Refresh />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Application Details Dialog */}
      <Dialog 
        open={!!selectedApplication && !reviewDialogOpen} 
        onClose={() => setSelectedApplication(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedApplication && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Person sx={{ mr: 1 }} />
                Application Details - {selectedApplication.firstName} {selectedApplication.lastName}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Personal Information
                </Typography>
                <Typography><strong>Name:</strong> {selectedApplication.firstName} {selectedApplication.lastName}</Typography>
                <Typography><strong>Email:</strong> {selectedApplication.email}</Typography>
                <Typography><strong>Date of Birth:</strong> {new Date(selectedApplication.dateOfBirth).toLocaleDateString()}</Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Institution Information
                </Typography>
                <Typography><strong>Institution:</strong> {selectedApplication.institution}</Typography>
              </Box>

              {selectedApplication.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Description sx={{ mr: 1 }} />
                    Description
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {selectedApplication.description}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {selectedApplication.verificationDocuments && selectedApplication.verificationDocuments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachFile sx={{ mr: 1 }} />
                    Verification Documents ({selectedApplication.verificationDocuments.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedApplication.verificationDocuments.map((doc, index) => (
                      <Paper key={index} elevation={0} sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{doc.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(doc.size / 1024 / 1024).toFixed(2)} MB • {doc.type}
                          </Typography>
                        </Box>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = doc.data;
                            link.download = doc.name;
                            link.click();
                          }}
                        >
                          Download
                        </Button>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}

              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Submitted:</strong> {formatDate(selectedApplication.submittedAt)}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedApplication(null)}>
                Close
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={() => handleReviewApplication(selectedApplication, 'reject')}
              >
                Reject
              </Button>
              <Button 
                variant="contained" 
                color="success"
                onClick={() => handleReviewApplication(selectedApplication, 'approve')}
              >
                Approve
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Review Dialog */}
      <Dialog 
        open={reviewDialogOpen} 
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {reviewAction === 'approve' ? 'Approve Application' : 'Reject Application'}
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Reviewing application from {selectedApplication.firstName} {selectedApplication.lastName}
              </Typography>
            </Box>
          )}
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label={reviewAction === 'approve' ? 'Approval Notes (Optional)' : 'Rejection Reason *'}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder={
              reviewAction === 'approve' 
                ? 'Welcome message or additional instructions...'
                : 'Please provide a reason for rejection...'
            }
            required={reviewAction === 'reject'}
            error={reviewAction === 'reject' && !reviewNotes.trim()}
            helperText={reviewAction === 'reject' && !reviewNotes.trim() ? 'Rejection reason is required' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            color={reviewAction === 'approve' ? 'success' : 'error'}
            onClick={handleSubmitReview}
            disabled={processing || (reviewAction === 'reject' && !reviewNotes.trim())}
          >
            {processing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              reviewAction === 'approve' ? 'Approve' : 'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResearcherApplications;
